import type { QueryMeta } from '@tanstack/react-query';
import { keepPreviousData as keepPreviousDataFn, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { z } from 'zod/v4';
import { routes } from '~/api/routes';
import { filtersToSearchParams } from '~/data-grid/utils/filters';
import { useSetGlobalPeriodFilter } from '~/global-state/filters';
import { useToast } from '~/global-state/toast';
import { useFetch } from '~/hooks/useFetch';
import { useFormatMessage } from '~/intl';
import type { LatestPeriod } from '~/schemas/metrics';
import { latestPeriodResponse } from '~/schemas/metrics';
import type { DateRangeString } from '~/utils/temporal';
import {
  type AllocationsQueryParams,
  allocationsPivotTableQueryParamsSchema,
  allocationsPivotTableResponseSchema,
} from './schemas/allocation-metrics.schemas';
import type {
  DemandCarbonRequest,
  DemandMatchingResolutionsRequest,
  DemandPlottingResolutionsRequest,
  DemandRequest,
} from './schemas/demand-metrics.schemas';
import {
  demandCarbonRequestSchema,
  demandCarbonResponseSchema,
  demandMatchingResolutionsRequestSchema,
  demandMatchingResolutionsResponseSchema,
  demandPivotTableResponseSchema,
  demandPlottingResolutionsRequestSchema,
  demandRequestSchema,
} from './schemas/demand-metrics.schemas';
import {
  demandFilterValuesResponse,
  plottingResolutionsResponseSchema,
  portfolioFilterValuesResponse,
  supplyFilterValuesResponse,
} from './schemas/metrics.schemas';
import {
  type PortfolioMatchingResolutionsRequest,
  type PortfolioPlottingResolutionsRequest,
  type PortfolioRequest,
  portfolioMatchingResolutionsRequestSchema,
  portfolioMatchingResolutionsResponseSchema,
  portfolioPivotTableResponseSchema,
  portfolioPlottingResolutionsRequestSchema,
  portfolioRequestSchema,
} from './schemas/portfolio-metrics.schemas';
import type { SupplyPlottingResolutionsRequest, SupplyRequest } from './schemas/supply-metrics.schemas';
import {
  supplyPivotTableResponseSchema,
  supplyPlottingResolutionsRequestSchema,
  supplyRequestSchema,
} from './schemas/supply-metrics.schemas';

type GetPerimeterQueryParams = {
  start_datetime?: string;
  end_datetime?: string;
};

type NullableQueryParams<T> = {
  [Key in keyof T]: T[Key] | null;
};

export const metricsQueryKeys = {
  getAllocationsPivotTable: (params: NullableQueryParams<AllocationsQueryParams>) => [
    ...metricsQueryKeys.root,
    'allocationsPivotTable',
    params,
  ],
  getDemand: (body?: DemandRequest) => [...metricsQueryKeys.root, 'demand', body],
  getDemandAssociatedFilterValues: () => [...metricsQueryKeys.root, 'demandAssociatedFilterValues'],
  getDemandCarbon: (body?: DemandCarbonRequest) => [...metricsQueryKeys.root, 'demandCarbon', body],
  getDemandFilterValues: () => [...metricsQueryKeys.root, 'demandFilterValues'],
  getDemandLatest: () => [...metricsQueryKeys.root, 'demandLatest'],
  getDemandMatchingResolutions: (body?: DemandMatchingResolutionsRequest) => [
    ...metricsQueryKeys.root,
    'demandMatchingResolutions',
    body,
  ],
  getDemandPivotTable: (utcDateRange: DateRangeString) => [...metricsQueryKeys.root, 'demandPivotTable', utcDateRange],
  getDemandPlottingResolutions: (body?: DemandPlottingResolutionsRequest) => [
    ...metricsQueryKeys.root,
    'activeDemandPlottingResolutions',
    body,
  ],
  getECDashboardAssociatedFilterValues: () => [...metricsQueryKeys.root, 'ecdashboardAssociatedFilterValues'],
  getPerimeter: (queryParams: GetPerimeterQueryParams) => [...metricsQueryKeys.root, 'perimeter', queryParams],
  getPortfolio: (body?: PortfolioRequest) => [...metricsQueryKeys.root, 'portfolio', body],
  getPortfolioFilterValues: () => [...metricsQueryKeys.root, 'portfolioFilterValues'],
  getPortfolioLatest: () => [...metricsQueryKeys.root, 'portfolioLatest'],
  getPortfolioMatchingResolutions: (body?: PortfolioMatchingResolutionsRequest) => [
    ...metricsQueryKeys.root,
    'portfolioMatchingResolutions',
    body,
  ],
  getPortfolioPivotTable: (utcDateRange: DateRangeString) => [
    ...metricsQueryKeys.root,
    'portfolioPivotTable',
    utcDateRange,
  ],
  getPortfolioPlottingResolutions: (body?: PortfolioPlottingResolutionsRequest) => [
    ...metricsQueryKeys.root,
    'activePortfolioPlottingResolutions',
    body,
  ],
  getSupply: (body?: SupplyRequest) => [...metricsQueryKeys.root, 'supply', body],
  getSupplyAssociatedFilterValues: (includeDispatched = false) => [
    ...metricsQueryKeys.root,
    'supplyAssociatedFilterValues',
    { includeDispatched },
  ],
  getSupplyFilterValues: () => [...metricsQueryKeys.root, 'supplyFilterValues'],
  getSupplyLatest: () => [...metricsQueryKeys.root, 'supplyLatest'],
  getSupplyPivotTable: (utcDateRange: DateRangeString) => [...metricsQueryKeys.root, 'supplyPivotTable', utcDateRange],
  getSupplyPlottingResolutions: (body?: SupplyPlottingResolutionsRequest) => [
    ...metricsQueryKeys.root,
    'activeSupplyPlottingResolutions',
    body,
  ],
  root: ['metrics'],
} as const;

export function useGetDemandLatest(enabled = true, shouldSetGlobalPeriodFilter = true) {
  const { fetchData } = useFetch();
  const setGlobalPeriodFilter = useSetGlobalPeriodFilter();

  const select = useCallback(
    (data: LatestPeriod) => {
      if (enabled && shouldSetGlobalPeriodFilter) {
        const { start_time, end_time } = data.month;
        setGlobalPeriodFilter([start_time, end_time]);
      }
      return data;
    },
    [enabled, shouldSetGlobalPeriodFilter, setGlobalPeriodFilter],
  );

  return useQuery({
    enabled,
    queryFn: () => fetchData(routes.metrics.getDemandLatest, { response: latestPeriodResponse }),
    queryKey: metricsQueryKeys.getDemandLatest(),
    select,
  });
}

export function useGetSupply<ResponseSchema extends z.ZodType>({
  body,
  enabled = true,
  keepPreviousData = false,
  responseSchema,
}: {
  body?: SupplyRequest;
  enabled?: boolean;
  /** Serve the previous body's data while the new one loads, instead of blanking out. */
  keepPreviousData?: boolean;
  responseSchema: ResponseSchema;
}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    placeholderData: keepPreviousData ? keepPreviousDataFn : undefined,
    queryFn: () =>
      fetchData(
        routes.metrics.getSupply,
        { request: supplyRequestSchema, response: responseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getSupply(body),
    retry: 1,
  });
}

export function useGetSupplyLatest(enabled = true, shouldSetGlobalPeriodFilter = true) {
  const { fetchData } = useFetch();
  const setGlobalPeriodFilter = useSetGlobalPeriodFilter();

  const select = useCallback(
    (data: LatestPeriod) => {
      if (enabled && shouldSetGlobalPeriodFilter) {
        const { start_time, end_time } = data.month;
        setGlobalPeriodFilter([start_time, end_time], true);
      }
      return data;
    },
    [enabled, shouldSetGlobalPeriodFilter, setGlobalPeriodFilter],
  );

  return useQuery({
    enabled,
    queryFn: () => fetchData(routes.metrics.getSupplyLatest, { response: latestPeriodResponse }),
    queryKey: metricsQueryKeys.getSupplyLatest(),
    select,
  });
}

export function useGetSupplyPlottingResolutions(body?: SupplyPlottingResolutionsRequest, enabled = true) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.metrics.getSupplyPlottingResolutions,
        { request: supplyPlottingResolutionsRequestSchema, response: plottingResolutionsResponseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getSupplyPlottingResolutions(body),
    select: (data) => data?.plotting_resolutions,
  });
}

export function useGetSupplyPivotTableData({
  periodStartFrom,
  periodStartTo,
}: {
  periodStartFrom: string | null;
  periodStartTo: string | null;
}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: !!periodStartFrom && !!periodStartTo,
    queryFn: () => {
      if (!periodStartFrom || !periodStartTo) {
        return undefined;
      }

      return fetchData(
        routes.metrics.getSupplyPivotTable,
        { response: supplyPivotTableResponseSchema },
        {
          queryParams: new URLSearchParams({
            period_start_from: periodStartFrom,
            period_start_to: periodStartTo,
          }),
        },
      );
    },
    queryKey: metricsQueryKeys.getSupplyPivotTable([periodStartFrom, periodStartTo]),
    retry: 1,
  });
}

export function useGetDemand<ResponseSchema extends z.ZodType>({
  body,
  enabled = true,
  keepPreviousData = false,
  responseSchema,
}: {
  body?: DemandRequest;
  enabled?: boolean;
  keepPreviousData?: boolean;
  responseSchema: ResponseSchema;
}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    placeholderData: keepPreviousData ? keepPreviousDataFn : undefined,
    queryFn: () =>
      fetchData(
        routes.metrics.getDemand,
        { request: demandRequestSchema, response: responseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getDemand(body),
    retry: 1,
  });
}

export function useGetDemandMatchingResolutions(body?: DemandMatchingResolutionsRequest, enabled = true) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.metrics.getDemandMatchingResolutions,
        { request: demandMatchingResolutionsRequestSchema, response: demandMatchingResolutionsResponseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getDemandMatchingResolutions(body),
  });
}

export function useGetDemandPlottingResolutions(body?: DemandPlottingResolutionsRequest, enabled = true) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.metrics.getDemandPlottingResolutions,
        { request: demandPlottingResolutionsRequestSchema, response: plottingResolutionsResponseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getDemandPlottingResolutions(body),
    select: (data) => data?.plotting_resolutions,
  });
}

export function useGetDemandCarbon(body?: DemandCarbonRequest, enabled = true) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.metrics.getDemandCarbon,
        { request: demandCarbonRequestSchema, response: demandCarbonResponseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getDemandCarbon(body),
  });
}

export function useGetDemandPivotTableData({
  keepPreviousData = false,
  periodStartFrom,
  periodStartTo,
}: {
  /** Serve the previous period's data while the new one loads, instead of blanking out. */
  keepPreviousData?: boolean;
  periodStartFrom: string | null;
  periodStartTo: string | null;
}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: !!periodStartFrom && !!periodStartTo,
    placeholderData: keepPreviousData ? keepPreviousDataFn : undefined,
    queryFn: () => {
      if (!periodStartFrom || !periodStartTo) {
        return undefined;
      }
      return fetchData(
        routes.metrics.getDemandPivotTable,
        { response: demandPivotTableResponseSchema },
        {
          queryParams: new URLSearchParams({
            period_start_from: periodStartFrom,
            period_start_to: periodStartTo,
          }),
        },
      );
    },
    queryKey: metricsQueryKeys.getDemandPivotTable([periodStartFrom, periodStartTo]),
    retry: 1,
  });
}

export function useGetPortfolio<ResponseSchema extends z.ZodType>({
  body,
  enabled = true,
  responseSchema,
}: {
  body?: PortfolioRequest;
  enabled?: boolean;
  responseSchema: ResponseSchema;
}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.metrics.getPortfolio,
        { request: portfolioRequestSchema, response: responseSchema },
        { body, method: 'POST' },
      ),
    queryKey: metricsQueryKeys.getPortfolio(body),
    retry: 1,
  });
}

export function useGetPortfolioLatest(
  enabled = true,
  shouldSetGlobalPeriodFilter = true,
  selectFormatter = (period: LatestPeriod) => period.month,
) {
  const { fetchData } = useFetch();
  const setGlobalPeriodFilter = useSetGlobalPeriodFilter();

  const select = useCallback(
    (data: LatestPeriod) => {
      if (enabled && shouldSetGlobalPeriodFilter) {
        const { start_time, end_time } = selectFormatter(data);
        setGlobalPeriodFilter([start_time, end_time]);
      }
      return selectFormatter(data);
    },
    [enabled, shouldSetGlobalPeriodFilter, selectFormatter, setGlobalPeriodFilter],
  );

  return useQuery({
    enabled,
    queryFn: () => fetchData(routes.metrics.getPortfolioLatest, { response: latestPeriodResponse }),
    queryKey: metricsQueryKeys.getPortfolioLatest(),
    select,
  });
}

export function useGetPortfolioPivotTableData({
  periodStartFrom,
  periodStartTo,
}: {
  periodStartFrom: string | null;
  periodStartTo: string | null;
}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: !!periodStartFrom && !!periodStartTo,
    queryFn: () => {
      if (!periodStartFrom || !periodStartTo) {
        return undefined;
      }

      return fetchData(
        routes.metrics.getPortfolioPivotTable,
        { response: portfolioPivotTableResponseSchema },
        {
          queryParams: new URLSearchParams({
            period_start_from: periodStartFrom,
            period_start_to: periodStartTo,
          }),
        },
      );
    },
    queryKey: metricsQueryKeys.getPortfolioPivotTable([periodStartFrom, periodStartTo]),
    retry: 1,
  });
}

export function useGetPortfolioMatchingResolutions(body?: PortfolioMatchingResolutionsRequest, enabled = true) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.metrics.getPortfolioMatchingResolutions,
        { request: portfolioMatchingResolutionsRequestSchema, response: portfolioMatchingResolutionsResponseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getPortfolioMatchingResolutions(body),
  });
}

export function useGetPortfolioPlottingResolutions(body?: PortfolioPlottingResolutionsRequest, enabled = true) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.metrics.getPortfolioPlottingResolutions,
        { request: portfolioPlottingResolutionsRequestSchema, response: plottingResolutionsResponseSchema },
        {
          body,
          method: 'POST',
        },
      ),
    queryKey: metricsQueryKeys.getPortfolioPlottingResolutions(body),
    select: ({ plotting_resolutions }) => plotting_resolutions,
  });
}

export function useGetSupplyFilterValues({ enabled = true }: { enabled?: boolean } = {}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () => fetchData(routes.metrics.getSupplyFilterValues, { response: supplyFilterValuesResponse }),
    queryKey: metricsQueryKeys.getSupplyFilterValues(),
  });
}

export function useGetDemandFilterValues({ enabled = true }: { enabled?: boolean } = {}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () => fetchData(routes.metrics.getDemandFilterValues, { response: demandFilterValuesResponse }),
    queryKey: metricsQueryKeys.getDemandFilterValues(),
  });
}

export function useGetPortfolioFilterValues() {
  const { fetchData } = useFetch();

  return useQuery({
    queryFn: () => fetchData(routes.metrics.getPortfolioFilterValues, { response: portfolioFilterValuesResponse }),
    queryKey: metricsQueryKeys.getPortfolioFilterValues(),
  });
}

type getAssociatedFilterValuesOptions<ResponseSchema> = {
  responseSchema: ResponseSchema;
  enabled?: boolean;
  meta?: QueryMeta;
};

export function useGetSupplyAssociatedFilterValues<ResponseSchema extends z.ZodType>({
  responseSchema,
  enabled = true,
  includeDispatched = false,
  meta = {},
}: getAssociatedFilterValuesOptions<ResponseSchema> & {
  /** Adds the dispatched supply rows, each carrying the registry transaction its supply was dispatched by. */
  includeDispatched?: boolean;
}) {
  const { fetchData } = useFetch();
  const { addToast } = useToast();
  const formatMessage = useFormatMessage();

  const showRetryToast = useCallback(() => {
    addToast({
      appearance: 'warning',
      description: formatMessage({ id: 'error.associatedFilterValues.message' }),
      id: 'demand-associated-filter-values-error',
      title: formatMessage({ id: 'error.associatedFilterValues.title' }),
    });
  }, [addToast, formatMessage]);

  return useQuery({
    enabled,
    meta: {
      customErrorHandler: showRetryToast,
      ...meta,
    },
    queryFn: () =>
      fetchData(
        routes.metrics.getSupplyAssociatedFilterValues,
        { response: responseSchema },
        includeDispatched ? { queryParams: new URLSearchParams({ include_dispatched: 'true' }) } : undefined,
      ),
    queryKey: metricsQueryKeys.getSupplyAssociatedFilterValues(includeDispatched),
  });
}

export function useGetDemandAssociatedFilterValues<ResponseSchema extends z.ZodType>({
  responseSchema,
  enabled = true,
  meta = {},
}: getAssociatedFilterValuesOptions<ResponseSchema>) {
  const { fetchData } = useFetch();
  const { addToast } = useToast();
  const formatMessage = useFormatMessage();

  const showRetryToast = useCallback(() => {
    addToast({
      appearance: 'warning',
      description: formatMessage({ id: 'error.associatedFilterValues.message' }),
      id: 'demand-associated-filter-values-error',
      title: formatMessage({ id: 'error.associatedFilterValues.title' }),
    });
  }, [addToast, formatMessage]);

  return useQuery({
    enabled,
    meta: {
      customErrorHandler: showRetryToast,
      ...meta,
    },
    queryFn: () =>
      fetchData(routes.metrics.getDemandAssociatedFilterValues, {
        response: responseSchema,
      }),
    queryKey: metricsQueryKeys.getDemandAssociatedFilterValues(),
  });
}

export function useGetAllocationsPivotTableData({
  period_start_from,
  period_start_to,
  demand_period_start_from,
  demand_period_start_to,
}: NullableQueryParams<AllocationsQueryParams>) {
  const { fetchData } = useFetch();

  const enabled = !!period_start_from && !!period_start_to;

  return useQuery({
    enabled,
    queryFn: () => {
      if (!period_start_from || !period_start_to) {
        return undefined;
      }

      const params = {
        period_start_from,
        period_start_to,
        ...(demand_period_start_from ? { demand_period_start_from } : {}),
        ...(demand_period_start_to ? { demand_period_start_to } : {}),
      };

      const queryParams = filtersToSearchParams(params, allocationsPivotTableQueryParamsSchema);

      return fetchData(
        routes.metrics.getAllocationsPivotTable,
        { response: allocationsPivotTableResponseSchema },
        { queryParams },
      );
    },
    queryKey: metricsQueryKeys.getAllocationsPivotTable({
      period_start_from,
      period_start_to,
      demand_period_start_from,
      demand_period_start_to,
    }),
    retry: 1,
  });
}

export function useGetECDashboardAssociatedFilterValues<ResponseSchema extends z.ZodType>({
  responseSchema,
  enabled = true,
  meta = {},
}: getAssociatedFilterValuesOptions<ResponseSchema>) {
  const { fetchData } = useFetch();
  const { addToast } = useToast();
  const formatMessage = useFormatMessage();

  const showRetryToast = useCallback(() => {
    addToast({
      appearance: 'warning',
      description: formatMessage({ id: 'error.associatedFilterValues.message' }),
      id: 'ecdashboard-associated-filter-values-error',
      title: formatMessage({ id: 'error.associatedFilterValues.title' }),
    });
  }, [addToast, formatMessage]);

  return useQuery({
    enabled,
    meta: {
      customErrorHandler: showRetryToast,
      ...meta,
    },
    queryFn: () =>
      fetchData(routes.metrics.getECDashboardAssociatedFilterValues, {
        response: responseSchema,
      }),
    queryKey: metricsQueryKeys.getECDashboardAssociatedFilterValues(),
  });
}
